import { useEndSearchSessionEffect } from '@ms/yammer-hooks/dist/searchSession';
import { useTranslations } from '@ms/yammer-i18n-react';
import CommonStrings from '@ms/yammer-strings/dist/Common';
import Block, { SemanticPaletteColor } from '@ms/yammer-web-core-ui/dist/components/Block';
import { ContainerWidthProvider } from '@ms/yammer-web-core-ui/dist/util/responsiveness';
import { useTeamsClientContext } from 'components/TeamsClientProvider/hooks';
import React, { FC, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { isInVivaConnectionsApp } from 'vivaConnections';

import { PageContextProvider } from '../PageContext';

import AppLayout from './LoadableAppLayout';
import {
  useCollapsiblePanelCallback,
  useHeaderCallback,
  useHeroBannerCallback,
  useLeftSidebarCallback,
  usePageLayoutContentRenderedCallback,
  usePageLayoutContentSidebarProps,
  usePageState,
  useSubHeaderCallback,
} from './hooks';

interface RoutePageLayoutProps {
  readonly collapseLeftSidebar?: boolean;
  readonly fullWidthLayout?: boolean;
  readonly noScroll?: boolean;
  readonly noSpacing?: boolean;
  readonly backgroundColor?: SemanticPaletteColor;
  onRenderFooter?(): ReactNode;
}

const Page: FC<RoutePageLayoutProps> = ({
  collapseLeftSidebar = false,
  noScroll = false,
  noSpacing = false,
  fullWidthLayout = false,
  backgroundColor,
  onRenderFooter,
  children,
}) => {
  const t = useTranslations(CommonStrings);
  const { pathname } = useLocation();
  const { shouldHideLeftSidebar } = useTeamsClientContext();
  const [pageState, { setShouldDisplayContentPanelButton }] = usePageState();
  const pageLayoutProps = usePageLayoutContentSidebarProps(pageState);

  const onRenderHeader = useHeaderCallback(collapseLeftSidebar);
  const onRenderSubHeader = useSubHeaderCallback();
  const onRenderHeroBanner = useHeroBannerCallback();
  const onRenderLeftSidebar = useLeftSidebarCallback();
  const onRenderRightCollapsiblePanel = useCollapsiblePanelCallback();
  const onPageLayoutContentRendered = usePageLayoutContentRenderedCallback(setShouldDisplayContentPanelButton);
  useEndSearchSessionEffect();
  const isInVivaConnections = isInVivaConnectionsApp();

  return (
    <PageContextProvider value={pageState}>
      <Block backgroundColor={backgroundColor}>
        <ContainerWidthProvider>
          <AppLayout
            {...pageLayoutProps}
            closeContentSidebarAriaLabel={t.commonClose()}
            collapseLeftSidebar={collapseLeftSidebar || shouldHideLeftSidebar}
            fullWidthLayout={fullWidthLayout}
            noScroll={noScroll}
            noSpacing={noSpacing}
            onRenderHeader={onRenderHeader}
            onRenderSubHeader={onRenderSubHeader}
            onRenderHeroBanner={onRenderHeroBanner}
            onRenderLeftSidebar={onRenderLeftSidebar}
            onRenderCollapsiblePanel={onRenderRightCollapsiblePanel}
            onRenderFooter={onRenderFooter}
            onPageLayoutContentRendered={onPageLayoutContentRendered}
            resetState={pathname}
            suppressTopPadding={isInVivaConnections}
          >
            {children}
          </AppLayout>
        </ContainerWidthProvider>
      </Block>
    </PageContextProvider>
  );
};

export default Page;
